import { Link } from "react-router-dom";
import {
  UtensilsCrossed, QrCode, Receipt, LayoutGrid, ShieldCheck, Smartphone,
  ArrowRight, Sparkles, IndianRupee, MessageCircle, Mail, Phone, ScanLine,
} from "lucide-react";

const QR_SCAN_IMG = "https://static.prod-images.emergentagent.com/jobs/f5e9d9ed-3b74-4ced-a554-6ecad4bf0067/images/045cfed50f48492b29b3f127d3562f027645285b6270ba446e9690e550630f6c.jpeg";
const CONTACTS = [
  { icon: Mail, label: "Email", value: "support.smartmenuqr@gmail.com", href: "mailto:support.smartmenuqr@gmail.com", id: "email" },
  { icon: Phone, label: "Phone 1", value: "+918822503796", href: "tel:+918822503796", id: "phone-1" },
  { icon: Phone, label: "Phone 2", value: "+917663941813", href: "tel:+917663941813", id: "phone-2" },
];
import { useAuth } from "@/context/AuthContext";

const features = [
  { icon: LayoutGrid, title: "Visual menu builder", desc: "Categories, photos, add-ons and variations with live availability toggles." },
  { icon: QrCode, title: "QR table ordering", desc: "Every table gets its own QR code. Guests scan, browse and order in seconds." },
  { icon: IndianRupee, title: "Correct pricing & GST", desc: "Server-validated totals, optional inclusive/exclusive GST, no double taxation." },
  { icon: Receipt, title: "Orders & PDF invoices", desc: "Live order board and professional invoices you can print or save." },
  { icon: MessageCircle, title: "WhatsApp sharing", desc: "Share formatted order summaries with one tap." },
  { icon: ShieldCheck, title: "Multi-tenant security", desc: "Each restaurant is fully isolated. Ownership enforced on every request." },
];

export default function Landing() {
  const { user } = useAuth();
  const home = user ? (user.role === "super_admin" ? "/admin" : user.role === "staff" ? "/staff" : "/dashboard") : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-4 sm:px-5">
          <div className="flex items-center gap-2 whitespace-nowrap font-heading text-lg font-bold text-slate-900 sm:text-xl">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500 text-white">
              <UtensilsCrossed className="h-5 w-5" />
            </span>
            <span className="hidden min-[420px]:inline">Smart Menu</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <a href="#contact" data-testid="nav-contact-btn" className="whitespace-nowrap rounded-full px-2 py-2 text-xs font-semibold sm:text-sm text-slate-700 hover:text-emerald-600 sm:px-4">
              Contact Us
            </a>
            {home ? (
              <Link to={home} data-testid="go-dashboard-btn" className="rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600">
                Go to dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="whitespace-nowrap rounded-full px-2 py-2 text-xs sm:px-4 sm:text-sm font-semibold text-slate-700 hover:text-slate-900">
                  Sign in
                </Link>
                <Link to="/register" data-testid="nav-register-btn" className="whitespace-nowrap rounded-full bg-emerald-500 px-3 py-2 text-xs sm:px-5 sm:text-sm font-semibold text-white transition hover:bg-emerald-600">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 lg:grid-cols-2 lg:py-24">
          <div className="sm-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles className="h-3.5 w-3.5" /> Restaurant SaaS · built for India
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Your menu, orders & billing — <span className="text-emerald-500">beautifully simple.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-slate-600 sm:text-lg">
              Launch a QR ordering experience, manage your menu with add-ons and variations, track live orders,
              and handle GST invoices — all from one clean dashboard.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register-btn" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/25 transition hover:bg-emerald-600">
                Create your restaurant, resort or hotel <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="/r/spice-junction" data-testid="hero-demo-menu-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-emerald-300">
                <Smartphone className="h-4 w-4" /> See a live demo menu
              </a>
            </div>
          </div>

          <div className="sm-fade-up relative">
            <div className="absolute -right-8 -top-8 h-64 w-64 rounded-full bg-emerald-200/40 blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-2xl shadow-slate-900/10">
              <img
                src="https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=900&q=80"
                alt="Signature dish"
                className="h-[420px] w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/90 p-4 backdrop-blur">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-slate-900">Butter Chicken</p>
                    <p className="text-xs text-slate-500">Medium spice · Full portion</p>
                  </div>
                  <p className="font-heading text-lg font-bold text-emerald-600">₹359</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-16" data-testid="scan-order-section">
        <div className="grid items-center gap-8 overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-500"><ScanLine className="h-5 w-5" /></span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-slate-900 sm:text-4xl">Scan. Order. <span className="text-emerald-500">Enjoy.</span></h2>
            <p className="mt-3 text-sm text-slate-500 sm:text-base">Guests scan the QR code on their table, browse your menu and order from their phone — no app, no waiting.</p>
          </div>
          <div className="lg:col-span-3">
            <img src={QR_SCAN_IMG} alt="Customer at a restaurant table scanning a QR code with a smartphone (illustration)" loading="lazy"
              className="aspect-[3/2] w-full rounded-2xl object-cover" data-testid="scan-order-image" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-slate-200 bg-white p-6 transition hover:-translate-y-1 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/5">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-500 transition group-hover:bg-emerald-500 group-hover:text-white">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-1.5 text-sm text-slate-500">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" className="mx-auto max-w-6xl scroll-mt-24 px-5 pb-20" data-testid="contact-section">
        <div className="rounded-3xl border border-emerald-100 bg-emerald-50/60 p-6 sm:p-8">
          <h2 className="font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Contact Us</h2>
          <p className="mt-2 text-sm text-slate-500">Questions about Smart Menu? We're happy to help.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            {CONTACTS.map((c) => (
              <a key={c.id} href={c.href} data-testid={`contact-${c.id}`}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-emerald-300">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white"><c.icon className="h-4 w-4" /></span>
                <span className="min-w-0"><span className="block text-xs text-slate-400">{c.label}</span>
                  <span className="block whitespace-nowrap text-[13px] font-semibold text-slate-800 sm:text-sm">{c.value}</span></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-6 text-sm text-slate-400 sm:flex-row">
          <p>© {new Date().getFullYear()} Smart Menu. Built for restaurants.</p>
          <div className="flex gap-4">
            <Link to="/login" className="hover:text-slate-600">Sign in</Link>
            <Link to="/register" className="hover:text-slate-600">Register</Link>
            <a href="#contact" className="hover:text-slate-600" data-testid="footer-contact-link">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
